import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { formatTextContent } from '../../services/funnelService';

const Container = styled.div`
  height: 100vh;
  overflow-y: scroll;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding: ${theme.spacing.screenPadding};
  padding-top: calc(${theme.spacing.screenPadding} + 24px); /* Account for progress bar */
  padding-bottom: calc(${theme.spacing.screenPadding} + 120px); /* Extra bottom padding for mobile CTA access */
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-bottom: calc(${theme.spacing.screenPadding} + 80px); /* Extra space to ensure CTA is scrollable + 20px buffer */
    height: 100vh;
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 100%;
  min-height: calc(100vh - ${theme.spacing.screenPadding} * 2 - 24px - 120px); /* Ensure content is tall enough to scroll */
  position: relative;
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: calc(100vh - ${theme.spacing.screenPadding} * 2 - 24px - 80px);
  }
`;

const TestimonialCard = styled.div`
  background: transparent;
  border-radius: 0;
  padding: ${theme.spacing.sm} ${theme.spacing.xxl} 0 ${theme.spacing.xxl};
  margin-bottom: ${theme.spacing.md};
  box-shadow: none;
  text-align: center;
  position: relative;
  border: none;
  
`;

const QuoteText = styled.div`
  font-family: 'Georgia', 'Times New Roman', 'serif';
  font-size: 1.2rem;
  color: ${theme.colors.doryBlack};
  line-height: ${theme.typography.lineHeights.relaxed};
  margin-bottom: 0;
  font-style: italic;
  padding: ${theme.spacing.lg} 0 ${theme.spacing.lg} 0;
  font-weight: 300;
  position: relative;
  z-index: 1; /* Normal stacking order */
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  
  /* Support for HTML tags */
  strong, b {
    font-weight: ${theme.typography.weights.semiBold};
    color: ${theme.colors.doryBlack};
  }
  
  em, i {
    font-style: italic;
  }
  
  @media screen and (max-width: ${theme.breakpoints.mobileLg}) {
    font-size: 1.2rem;
    padding: ${theme.spacing.md} 0 ${theme.spacing.md} 0;
  }
  
  @media screen and (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.1rem;
  }
`;

const AuthorSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.md};
  margin-top: 0;
  
  @media screen and (max-width: ${theme.breakpoints.tablet}) {
    flex-direction: column;
  }
`;

const AuthorImage = styled.div`
  width: 100px;
  height: 100px;
  border-radius: ${theme.borderRadius.full};
  background: linear-gradient(135deg, ${theme.colors.doryLighterBlue} 0%, ${theme.colors.doryBlue} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 4px solid ${theme.colors.doryBlue};
  box-shadow: ${theme.shadows.md};
  flex-shrink: 0;
  position: relative;
  
  /* Add a subtle inner shadow for depth */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: ${theme.borderRadius.full};
    box-shadow: inset 0 2px 4px rgba(64, 92, 115, 0.2);
    pointer-events: none;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: right bottom;
    border-radius: ${theme.borderRadius.full};
    position: absolute;
    z-index: 1;
  }
  
  /* Fallback styling when no image - only show when there's no img child */
  &:not(:has(img))::after {
    content: '👤';
    font-size: 2.5rem;
    color: white;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
  }
`;

const AuthorInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

const AuthorName = styled.div`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.doryBlue};
  text-align: center;
  margin-bottom: ${theme.spacing.xs};
`;

const AuthorTitle = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.doryGreen};
  text-align: center;
  font-weight: ${theme.typography.weights.medium};
`;

// Bar Visualization Components (matching Mental Resilience style)
const BarContainer = styled.div`
  width: 100%;
  max-width: 80vw;
  margin: 60px auto 20px auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
`;

const BarWrapper = styled.div`
  width: 80vw;
  max-width: 80vw;
  height: 40px;
  position: relative;
  background: #B8C5D1;
  border-radius: ${theme.borderRadius.lg};
  overflow: visible;
  box-shadow: ${theme.shadows.sm};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 80vw;
    max-width: 80vw;
  }
`;

const ScoreIndicator = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 12px;
  height: 12px;
  background: ${props => props.color || theme.colors.text};
  border: 3px solid white;
  border-radius: 50%;
  z-index: 3;
  box-shadow: ${theme.shadows.md};
  transition: all ${theme.animation.durations.normal} ${theme.animation.easings.easeOut};
`;

const ScoreLabel = styled.div`
  position: absolute;
  top: -43px;
  left: 50%;
  transform: translateX(-50%);
  background: ${props => props.backgroundColor || theme.colors.text};
  color: white;
  padding: 6px 12px;
  border-radius: ${theme.borderRadius.sm};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  font-weight: ${theme.typography.weights.semiBold};
  white-space: nowrap;
  z-index: 4;
  box-shadow: ${theme.shadows.sm};
  
  /* Arrow pointing down */
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: ${props => props.backgroundColor || theme.colors.text};
  }
`;

const BarLabels = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.doryDarkerBlue};
  font-weight: ${theme.typography.weights.medium};
  pointer-events: none;
  z-index: 5;
`;

const BarLabel = styled.span`
  position: absolute;
  text-align: center;
  min-width: 20px;
  transform: translateX(-50%) translateY(-50%);
  top: 50%;
`;

const SVGLine = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
`;



const ContinueButton = styled.button`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.touch};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  min-height: 48px; /* Ensure good touch target size */
  box-shadow: ${theme.shadows.sm};
  margin-top: 0;
  margin-bottom: 24px;
  min-width: 200px;
  width: fit-content;
  max-width: 90%;
  align-self: center;
  white-space: normal;
  line-height: 1.2;
  text-align: center;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  position: relative;
  z-index: 10;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: ${theme.shadows.sm};
  }
  
  &:disabled {
    background: ${theme.colors.disabled};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: 56px; /* Larger touch target on mobile */
    padding: 1rem 1.5rem; /* More padding for better touch experience */
  }
  
  /* For very small screens, ensure text fits */
  @media (max-width: 320px) {
    font-size: 0.875rem;
    padding: 0.75rem 1rem 2.0rem 1rem;
    min-width: 150px;
  }
`;

const TestimonialScreen = ({ data, onNext, userProgress = {} }) => {
  const [optimalPaceData, setOptimalPaceData] = useState(userProgress.calculatedMetrics?.optimalPace);
  
  // Update optimal pace data when userProgress changes
  useEffect(() => {
    const newOptimalPaceData = userProgress.calculatedMetrics?.optimalPace;
    if (newOptimalPaceData) {
      console.log('TestimonialScreen: Received optimal pace data', newOptimalPaceData);
      setOptimalPaceData(newOptimalPaceData);
    }
  }, [userProgress]);

  const handleContinue = () => {
    onNext?.(data.key);
  };

  // Get formatted HTML content for the testimonial quote
  const htmlContent = formatTextContent(data.content?.text, userProgress);

  // Helper function to render the bar visualization
  const renderBarVisualization = (startPace, endPace) => {
    // If we have both start and end pace values, show both dots
    if (startPace !== undefined && startPace !== null && endPace !== undefined && endPace !== null) {
      const startPosition = Math.max(0, Math.min(18, startPace));
      const endPosition = Math.max(0, Math.min(18, endPace));
      // Move dots inward from bar ends (5% from left, 5% from right)
      const startIndicatorLeft = `${2 + (startPosition / 18) * 95}%`;
      const endIndicatorLeft = `${2 + (endPosition / 18) * 95}%`;

      return (
        <BarContainer>
          <BarWrapper>
            <BarLabels>
              {/* Show both labels when both should be visible */}
              {!(startPace <= 1 || endPace <= 1) && !(startPace >= 17 || endPace >= 17) && (
                <>
                  <BarLabel style={{ left: '20px' }}>0</BarLabel>
                  <BarLabel style={{ right: '5px' }}>18</BarLabel>
                </>
              )}
              {/* Show only "18" label (near right end) when "0" is hidden */}
              {(startPace <= 1 || endPace <= 1) && !(startPace >= 17 || endPace >= 17) && (
                <BarLabel style={{ right: '5px' }}>18</BarLabel>
              )}
              {/* Show only "0" label (centered) when "18" is hidden */}
              {!(startPace <= 1 || endPace <= 1) && (startPace >= 17 || endPace >= 17) && (
                <BarLabel style={{ left: '20px' }}>0</BarLabel>
              )}
            </BarLabels>
            
            {/* Start Pace Indicator */}
            <ScoreIndicator 
              style={{ left: startIndicatorLeft }} 
              color={theme.colors.doryBlue}
            >
              <ScoreLabel backgroundColor={theme.colors.doryBlue}>
                {Math.round(startPace)}
              </ScoreLabel>
            </ScoreIndicator>
            
            {/* End Pace Indicator */}
            <ScoreIndicator 
              style={{ left: endIndicatorLeft }} 
              color={theme.colors.doryGreen}
            >
              <ScoreLabel backgroundColor={theme.colors.doryGreen}>
                {Math.round(endPace)}
              </ScoreLabel>
            </ScoreIndicator>
            
            {/* Line connecting the two dots */}
            <SVGLine>
              <line
                x1={startIndicatorLeft}
                y1="50%"
                x2={endIndicatorLeft}
                y2="50%"
                stroke={theme.colors.doryDarkerBlue}
                strokeWidth="6"
              />
            </SVGLine>
          </BarWrapper>
        </BarContainer>
      );
    }
    
    // Fallback to single optimal pace if only one value is available
    const fallbackValue = startPace !== undefined ? startPace : endPace;
    if (fallbackValue === undefined || fallbackValue === null) return null;

    const scorePosition = Math.max(0, Math.min(18, fallbackValue));
    const indicatorLeft = `${(scorePosition / 18) * 100}%`;

    return (
      <BarContainer>
        <BarWrapper>
          <BarLabels>
            <BarLabel>0</BarLabel>
            <BarLabel>18</BarLabel>
          </BarLabels>
          
          <ScoreIndicator style={{ left: indicatorLeft }}>
            <ScoreLabel>{Math.round(fallbackValue)}</ScoreLabel>
          </ScoreIndicator>
        </BarWrapper>
      </BarContainer>
    );
  };



  // Check if we have optimal pace data for chart display
  const { optimalHours } = optimalPaceData || {};
  
  // Get Start Pace and End Pace from the testimonial screen data
  const startPace = data.content?.startPace;
  const endPace = data.content?.endPace;

  return (
    <Container>
      <Content>
        <TestimonialCard>
          <AuthorSection>
            <AuthorImage>
              {data.content?.authorImage && (
                <img 
                  src={data.content.authorImage} 
                  alt={data.content?.authorName || 'Author'} 
                />
              )}
            </AuthorImage>
            
            <AuthorInfo>
              {data.content?.authorName && (
                <AuthorName>{data.content.authorName}</AuthorName>
              )}
              {data.content?.authorTitle && (
                <AuthorTitle>{data.content.authorTitle}</AuthorTitle>
              )}
            </AuthorInfo>
          </AuthorSection>
          
          {/* Bar visualization with Start Pace and End Pace */}
          {renderBarVisualization(startPace, endPace)}
          
          {htmlContent && (
            <QuoteText dangerouslySetInnerHTML={{ __html: htmlContent }} />
          )}
          
        </TestimonialCard>
        
        {/* CTA button appears right after testimonial */}
        <ContinueButton onClick={handleContinue}>
          {data.content?.ctaText || 'Continue'}
        </ContinueButton>
      </Content>
    </Container>
  );
};

export default TestimonialScreen;
